import React, { useState, useRef } from 'react';
import { ImageBox } from './BoxedImage';
import { Rect } from '../types';
import {
  getCornersFromShape,
  getShapeFromCorners,
  Coords,
} from '../utils/detectionEditor';
import { getContainedSVGCoordinates } from '../utils/getContainedSVGCoordinates';

interface DragOverlayProps {
  box: ImageBox;
  imageWidth: number;
  imageHeight: number;
  imageSrc: string;
  styles?: React.CSSProperties | undefined;
  onDetectionEdited?: (updatedDetection: ImageBox) => void;
  onMouseUpWhileDragging?: () => void;
  onBoxClick?: (detection: ImageBox) => void;
  onBoxClear: () => void;
  onMagnifierPositionChange?: (
    position: { x: number; y: number; clientX: number } | null
  ) => void;
}

const DEFAULT_STROKE_WIDTH = 2;

/**
 * DragOverlay: Renders a single detection box as an overlay with full edit interaction support.
 * This component handles all mouse events for editing, preventing re-rendering of other boxes.
 */
export const DragOverlay: React.FC<DragOverlayProps> = ({
  box,
  imageWidth,
  imageHeight,
  imageSrc,
  styles,
  onDetectionEdited,
  onMouseUpWhileDragging,
  onBoxClick,
  onBoxClear,
  onMagnifierPositionChange,
}) => {
  const viewBox = `0 0 ${imageWidth} ${imageHeight}`;
  const imgRef = React.useRef<SVGImageElement>(null);
  const [draggingCornerIndex, setDraggingCornerIndex] = useState<number | null>(
    null
  );
  const originalCornersRef = useRef<Coords[]>([]);

  // Helper function to convert mouse coordinates to SVG coordinates
  const getSVGCoordinates = (e: MouseEvent): { x: number; y: number } => {
    return getContainedSVGCoordinates(e, imgRef.current, viewBox);
  };

  // Corner dragging handlers
  const handleCornerDragMove = (e: MouseEvent) => {
    if (draggingCornerIndex === null) {
      return;
    }

    const svgCoords = getSVGCoordinates(
      'nativeEvent' in e ? (e.nativeEvent as MouseEvent) : (e as any)
    );

    // Update magnifier position with both SVG coords and raw clientX
    if (onMagnifierPositionChange) {
      onMagnifierPositionChange({
        x: svgCoords.x,
        y: svgCoords.y,
        clientX: e.clientX,
      });
    }

    const newCorners = [...originalCornersRef.current];

    // Handle polygon boxes differently from rectangle boxes
    if (box.isPolygon) {
      // For polygons, update points directly
      newCorners[draggingCornerIndex] = svgCoords;

      // Calculate bounding box from polygon points for the shape property
      const xs = newCorners.map((c) => c.x);
      const ys = newCorners.map((c) => c.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      const maxX = Math.max(...xs);
      const maxY = Math.max(...ys);
      const newShape: Rect = [minX, minY, maxX - minX, maxY - minY];

      const updatedBox = {
        ...box,
        points: newCorners, // Update the points array for polygons
        shape: newShape, // Update bounding box
      };

      // Notify parent of the updated box
      if (onDetectionEdited) {
        onDetectionEdited(updatedBox);
      }
    } else {
      // Rectangle mode: update adjacent corners to maintain rectangle shape
      // Corner order: 0=top-left, 1=top-right, 2=bottom-right, 3=bottom-left
      switch (draggingCornerIndex) {
        case 0: // top-left: update top and left edges
          newCorners[0] = svgCoords; // top-left gets full update
          newCorners[1] = { x: newCorners[1].x, y: svgCoords.y }; // top-right gets Y
          newCorners[3] = { x: svgCoords.x, y: newCorners[3].y }; // bottom-left gets X
          break;
        case 1: // top-right: update top and right edges
          newCorners[1] = svgCoords; // top-right gets full update
          newCorners[0] = { x: newCorners[0].x, y: svgCoords.y }; // top-left gets Y
          newCorners[2] = { x: svgCoords.x, y: newCorners[2].y }; // bottom-right gets X
          break;
        case 2: // bottom-right: update bottom and right edges
          newCorners[2] = svgCoords; // bottom-right gets full update
          newCorners[3] = { x: newCorners[3].x, y: svgCoords.y }; // bottom-left gets Y
          newCorners[1] = { x: svgCoords.x, y: newCorners[1].y }; // top-right gets X
          break;
        case 3: // bottom-left: update bottom and left edges
          newCorners[3] = svgCoords; // bottom-left gets full update
          newCorners[2] = { x: newCorners[2].x, y: svgCoords.y }; // bottom-right gets Y
          newCorners[0] = { x: svgCoords.x, y: newCorners[0].y }; // top-left gets X
          break;
      }

      const newShape = getShapeFromCorners(newCorners);

      const updatedBox = {
        ...box,
        shape: newShape,
      };

      // Notify parent of the updated box
      if (onDetectionEdited) {
        onDetectionEdited(updatedBox);
      }
    }
  };

  const handleCornerDragEnd = () => {
    setDraggingCornerIndex(null);
    if (onMagnifierPositionChange) {
      onMagnifierPositionChange(null);
    }
    if (onMouseUpWhileDragging) {
      onMouseUpWhileDragging();
    }
  };

  const handleCornerMouseDown = (e: React.MouseEvent, cornerIndex: number) => {
    e.stopPropagation();
    e.preventDefault();
    setDraggingCornerIndex(cornerIndex);

    // Store original corners based on box type
    originalCornersRef.current =
      box.isPolygon && box.points
        ? [...box.points]
        : getCornersFromShape(box.shape);
  };

  const handleBoxClick = (e: React.MouseEvent) => {
    // Only handle clicks on the box body (not corners)
    if ((e.target as SVGElement).tagName !== 'circle') {
      if (onBoxClick) {
        onBoxClick(box);
      }
    }
  };

  // Calculate corners for rendering
  const corners =
    box.isPolygon && box.points
      ? box.points
      : [
          { x: box.shape[0], y: box.shape[1] },
          { x: box.shape[0] + box.shape[2], y: box.shape[1] },
          { x: box.shape[0] + box.shape[2], y: box.shape[1] + box.shape[3] },
          { x: box.shape[0], y: box.shape[1] + box.shape[3] },
        ];

  const onMouseDown = (e: MouseEvent) => {
    onBoxClear();
  };

  const onMouseMove = (e: MouseEvent) => {
    if (draggingCornerIndex !== null) {
      handleCornerDragMove(e);
      return;
    }
  };

  const onMouseUp = (e: MouseEvent) => {
    if (draggingCornerIndex !== null) {
      handleCornerDragEnd();
      return;
    }
  };

  return (
    <svg
      className="w-full"
      viewBox={viewBox}
      style={{
        ...styles,
        pointerEvents: 'auto',
      }}
      onMouseDown={(e) => onMouseDown(e.nativeEvent)}
      onMouseMove={(e) => onMouseMove(e.nativeEvent)}
      onMouseUp={(e) => onMouseUp(e.nativeEvent)}
    >
      {/* Render invisible image to establish coordinate system matching BoxedImage */}
      <image
        ref={imgRef}
        href={imageSrc}
        width={imageWidth}
        height={imageHeight}
        opacity={0}
        style={{ pointerEvents: 'none' }}
      />

      {box.isPolygon && box.points ? (
        // Render polygon
        <polygon
          points={box.points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill={box.color}
          fillOpacity={box.opacity ?? 0.2}
          stroke={box.color}
          strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
          style={{ cursor: 'pointer' }}
          onClick={handleBoxClick}
        />
      ) : (
        // Render rectangle
        box && (
          <rect
            x={box.shape[0]}
            y={box.shape[1]}
            width={box.shape[2]}
            height={box.shape[3]}
            fill={box.color}
            fillOpacity={box.opacity ?? 0.2}
            stroke={box.color}
            strokeWidth={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
            rx={box.strokeWidth ?? DEFAULT_STROKE_WIDTH}
            style={{ cursor: 'pointer' }}
            onClick={handleBoxClick}
          />
        )
      )}

      {/* Render corner handles for editing */}
      {corners.map((corner, index) => (
        <circle
          key={`corner-${index}`}
          cx={corner.x}
          cy={corner.y}
          r={50}
          fill={box.color}
          fillOpacity={0.7}
          stroke="white"
          strokeWidth={6}
          style={{ cursor: 'crosshair', pointerEvents: 'auto' }}
          onMouseDown={(e) => handleCornerMouseDown(e, index)}
        />
      ))}
    </svg>
  );
};
